import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Card } from '../ui';

export default function QuestionCard({
  questionNumber,
  totalQuestions,
  prompt,
  guidance,
  questionType,
  children,
  className,
}) {
  const { t } = useTranslation();
  const getTypeLabel = (type) => {
    const labels = {
      short_answer: 'Short answer',
      multiple_choice: 'Multiple choice',
      essay: 'Essay',
      table_completion: 'Categorization',
    };
    return labels[type] || type;
  };

  return (
    <Card className={cn('transition-all duration-200', className)}>
      {(questionNumber || totalQuestions) && (
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('questions.questionOf', { current: questionNumber, total: totalQuestions })}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-lg sm:text-xl font-medium text-gray-900 dark:text-white leading-relaxed">
          {prompt}
        </h2>
      </div>

      <div className="mb-6">
        {children}
      </div>

      {(guidance || questionType) && (
        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-3">
          {questionType && (
            <span className="capitalize">{getTypeLabel(questionType)}</span>
          )}
          {questionType && guidance && <span>&#8226;</span>}
          {guidance && <span>{guidance}</span>}
        </div>
      )}
    </Card>
  );
}

export function QuestionNavigation({
  currentIndex,
  totalQuestions,
  onPrevious,
  onNext,
  canGoNext = true,
  nextLabel = 'Next',
  className,
}) {
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalQuestions - 1;

  return (
    <div className={cn('flex items-center justify-between', className)}>
      <button
        onClick={onPrevious}
        disabled={isFirst}
        className={cn(
          'inline-flex items-center px-4 py-2 rounded-lg',
          'text-sm font-medium',
          'transition-all duration-200',
          isFirst
            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700'
        )}
      >
        <svg
          className="w-4 h-4 mr-1.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Previous
      </button>

      <div className="flex items-center gap-1.5">
        {Array.from({ length: totalQuestions }).map((_, index) => (
          <span
            key={index}
            className={cn(
              'w-2 h-2 rounded-full transition-all duration-200',
              index === currentIndex
                ? 'bg-gray-900 dark:bg-white w-4'
                : 'bg-gray-300 dark:bg-gray-600'
            )}
          />
        ))}
      </div>

      <button
        onClick={onNext}
        disabled={!canGoNext}
        className={cn(
          'inline-flex items-center px-4 py-2 rounded-lg',
          'text-sm font-medium',
          'transition-all duration-200',
          !canGoNext
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-b from-gray-800 to-black dark:from-white dark:to-gray-200 text-white dark:text-gray-900 shadow-[0_2px_8px_rgba(0,0,0,0.2)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.3)]'
        )}
      >
        {isLast ? 'Submit' : nextLabel}
        <svg
          className="w-4 h-4 ml-1.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>
  );
}
